import React, { useEffect, useLayoutEffect, useState, useContext, useRef } from 'react';
import { AppContext } from '../context/AppContext.jsx';
import axios from 'axios';
import { useLanguage } from "../i18n/LanguageContext.jsx";
import i18n from "../i18n/i18n.js";
import { Link } from 'react-router-dom';
import { getFirstImage, slugify, getSlug } from '../helpers/HelperFunctions.js';
import gsap from 'gsap';
import ScrollTrigger from '../../gsap/ScrollTrigger.min.js';
gsap.registerPlugin(ScrollTrigger);

const ProgettiList = ({ articles, pageId }) => {
    const { language } = useLanguage();
    const { siteMap } = useContext(AppContext);
    const pageUrl = `/${language}${process.env.REACT_APP_API_PAGE_URL}`;
    const [pageTitle, setPageTitle] = useState(null);
    const [componentLoaded, setComponentLoaded] = useState(false);
    const imageRefs = useRef([]);
    const textRefs = useRef([]);


    useEffect(() => {
        if (!pageId) return;
        axios.get(`${pageUrl}/${pageId}`)
            .then(res => {
                setPageTitle(res.data.title || null);
            })
            .catch(err => {
                console.error('Errore durante il fetch della pagina:', err);
            }
            );
    }, [pageUrl, pageId]);

    useEffect(() => {
        if (pageTitle && siteMap) {
            setComponentLoaded(true);
        } else {
            setComponentLoaded(false);
        }
    }, [pageTitle, siteMap]);

    const getFirstWord = (text) => {
        if (!text) return '';
        const words = text.trim().split(' ');
        return words.length > 0 ? words[0] : '';
    };

    const getRemainingWords = (text) => {
        if (!text) return '';
        const words = text.trim().split(' ');
        return words.length > 1 ? words.slice(1).join(' ') : '';
    };

    useEffect(() => {
        if (!componentLoaded || !articles?.items) return;

        textRefs.current.forEach((txt, index) => {
            if (!txt) return;

            gsap.from(txt, {
                scrollTrigger: {
                    trigger: txt,
                    start: "top bottom-=200px",
                    toggleActions: "play none none none",
                    id: `textBlur-${index}`,
                    delay: 0.3,
                    /*markers: true,*/
                },
                y: 100,
                opacity: 0,
                duration: 1,
                ease: 'power2.out',
            });
        });

        return () => {
            textRefs.current.forEach((txt, index) => {
                ScrollTrigger.getById(`textBlur-${index}`)?.kill();
            });
        };
    }, [componentLoaded, articles]);

    useEffect(() => {
        if (!componentLoaded || !articles?.items) return;

        imageRefs.current.forEach((img, index) => {
            if (!img) return;

            gsap.from(img, {
                scrollTrigger: {
                    trigger: img,
                    start: "top bottom-=200px",
                    toggleActions: "play none none none",
                    id: `cardBlur-${index}`,
                    /*markers: true,*/
                },
                y: 100,
                opacity: 0,
                duration: 1,
                ease: 'power2.out',
            });
        });

        return () => {
            imageRefs.current.forEach((img, index) => {
                ScrollTrigger.getById(`cardBlur-${index}`)?.kill();
            });
        };
    }, [componentLoaded, articles]);

    useEffect(() => {
        if (!componentLoaded || !articles?.items) return;
        const magicHr = document.querySelector('.magic-hr');

        gsap.from(magicHr, {
            scrollTrigger: {
                trigger: magicHr,
                start: "top bottom-=200px",
                toggleActions: "play none none none",
                id: `magic-hr`,
                /* markers: true,*/
            },
            x: "-120%",
            opacity: 0,
            duration: 2,
            ease: 'power2.out',
        });

        return () => {
            ScrollTrigger.getById(`magic-hr`)?.kill();
        };
    }, [componentLoaded, articles]);

    return (
        <>
            {pageTitle && pageId && (
                <div className="container-xl">
                    <div className="row justify-content-between align-items-center position-relative">
                        <div className="col-12">
                            <h2 className="bg-white br-lg d-inline-block fs-1 lh-1 mb-0 pe-4 pe-md-10">
                                <span className="fw-bold">{getFirstWord(pageTitle)}</span>
                                <span className="fw-light"> {getRemainingWords(pageTitle)}</span>
                            </h2>
                        </div>
                        <div className="col-12 align-items-center overflow-hidden d-flex justify-content-center position-absolute py-2 z-n1">
                            <hr className="mb-0 mt-0 opacity-100 magic-hr w-100" />
                        </div>
                    </div>
                </div>
            )}
            {articles && articles?.items && (
                <div className="progetti-list-container container-xl">
                    <div className="row mt-5 gy-5">
                        {articles.items.map((article, index) => (
                            <div key={article.id} className="col-12 mb-7 mb-lg-0">
                                <div className="container-xl p-0">
                                    <div className="row align-items-center">
                                        <div className="col-12 col-lg-5 mb-4 mb-lg-0">
                                            {article.media && article.media.length > 0 && (
                                                <Link to={`${siteMap['1a0fdc56-4c54-4ca5-886e-76b559428500'].path}/${getSlug(article)}`} className="d-block">
                                                    <img
                                                        ref={(el) => imageRefs.current[index] = el}
                                                        src={article.media[0].urlFullHD}
                                                        alt={article.name}
                                                        className="w-100 h-auto object-fit-cover"
                                                        style={{ aspectRatio: '6 / 3' }}
                                                    />
                                                </Link>
                                            )}
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <div className="ps-lg-5" ref={(el) => textRefs.current[index] = el}>
                                                {/*{article.tagNames && article.tagNames.length > 0 && (*/}
                                                {/*    <p className="text-muted mb-3 small font-secondary fw-light fs-6">{article.tagNames[0]}</p>*/}
                                                {/*)}*/}
                                                {article.subtitle && ( 
                                                    <p className="text-muted mb-3 small font-secondary fw-light fs-6">{article.subtitle}</p>
                                                )}
                                                <h3 className="mb-3 lh-1 fs-1">
                                                    <span className="fw-light">{i18n[language].project}</span><br />
                                                    <span className="fw-bold">{article.title}</span>
                                                </h3>
                                                <Link
                                                    to={`${siteMap['1a0fdc56-4c54-4ca5-886e-76b559428500'].path}/${getSlug(article)}`}
                                                    className="font-secondary align-items-center bg-black btn d-flex justify-content-between px-4 rounded-0 text-uppercase text-white w-fitcontent mt-4">
                                                    {i18n[language].discover}
                                                    <svg width="8" height="13" viewBox="0 0 8 13" fill="none" className="ms-3" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M0.339111 0.367432L6.83911 6.36743L0.839111 12.3674" stroke="white" />
                                                    </svg>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default ProgettiList;